/**
 * 업무 상태 전이 유틸 (권한 매트릭스 기반).
 * 서버 feature_key 포맷: `project.status:{FROM}>{TO}:{event}`
 */

/**
 * release ProjectStatus.vue 의 projectStatusArr 복원본 (폴백용).
 * event `returned` 는 매트릭스 키 `return` 으로 정규화.
 * step8→step9 등 상태표에 없는 전이는 시드(seed-permission) 기준으로 제외.
 *
 * 원본 문자열 예: cs_step1_step2_approval → CS / WORK_ACCEPTED>WORK_REQUEST:approval
 */
const LEGACY_TRANSITIONS = [
	{ role: 'CS', key: 'project.status:WORK_ACCEPTED>WORK_REQUEST:approval' },
	{ role: 'CS_SUB_ADMIN', key: 'project.status:WORK_ACCEPTED>WORK_REQUEST:approval' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_ACCEPTED>WORK_REQUEST:approval' },
	{ role: 'CS', key: 'project.status:WORK_REQUEST>WORK_ACCEPTED:collect' },
	{ role: 'CS_SUB_ADMIN', key: 'project.status:WORK_REQUEST>WORK_ACCEPTED:collect' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_REQUEST>WORK_ACCEPTED:collect' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_REQUEST>WORK_ACCEPTED:return' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_REQUEST>WORK_ANALYSIS:approval' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_ANALYSIS>WORK_REQUEST:collect' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_ANALYSIS>WORK_ANALYSIS_COMPLETE:approval' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_ANALYSIS_COMPLETE>WORK_ANALYSIS:collect' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_ANALYSIS_COMPLETE>WORK_DIRECTOR_REVIEW:approval' },
	{ role: 'CS_SUB_ADMIN', key: 'project.status:WORK_DIRECTOR_REVIEW>WORK_ANALYSIS_COMPLETE:return' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_DIRECTOR_REVIEW>WORK_ANALYSIS_COMPLETE:return' },
	{ role: 'CS_SUB_ADMIN', key: 'project.status:WORK_DIRECTOR_REVIEW>WORK_IMPLEMENTATION_REVIEW:approval' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_DIRECTOR_REVIEW>WORK_IMPLEMENTATION_REVIEW:approval' },
	{ role: 'TECH_ADMIN', key: 'project.status:WORK_DIRECTOR_REVIEW>WORK_ANALYSIS_COMPLETE:collect' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_IMPLEMENTATION_REVIEW>WORK_DIRECTOR_REVIEW:return' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_IMPLEMENTATION_REVIEW>WORK_APPROVAL:approval' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_APPROVAL>WORK_IMPLEMENTATION_REVIEW:collect' },
	{ role: 'CS', key: 'project.status:WORK_APPROVAL>WORK_COMPLETE:approval' },
	{ role: 'CS_SUB_ADMIN', key: 'project.status:WORK_APPROVAL>WORK_COMPLETE:approval' },
	{ role: 'CS_ADMIN', key: 'project.status:WORK_APPROVAL>WORK_COMPLETE:approval' }
]

/**
 * «모든 단계 변경 허용» 와일드카드 키 (서버 lib/permission.js 와 같은 값).
 * 이 키를 가진 역할은 개별 전이 목록과 무관하게 모든 단계로 이동할 수 있다.
 */
const STATUS_WILDCARD = 'project.status:*'

/** 전이 목록에 와일드카드가 들어 있는가 */
function hasStatusWildcard(statusTransitions) {
	return Array.isArray(statusTransitions) && statusTransitions.includes(STATUS_WILDCARD)
}

/** feature_key 파싱 → { from, to, event } (형식 불일치 시 null) */
function parseTransitionKey(key) {
	const m = /^project\.status:([^>]+)>([^:]+):(.+)$/.exec(key)
	if (!m) return null
	return { from: m[1], to: m[2], event: m[3] }
}

/** 시드 이벤트 토큰(approval/collect/return) → project_issue 키(returned 포함) 정규화 */
function normalizeEvent(event) {
	return event === 'return' ? 'returned' : event
}

/**
 * 와일드카드 보유 역할용: 현재 상태 제외 전체 상태로의 자유 전이 목록.
 * event 는 approval 로 통일(관리자 이슈 확인창 스킵과 맞춤).
 */
function deriveAdminAvailableTransitions(fromValue, projectStatuses) {
	const list = projectStatuses || []
	const current = list.find((el) => el.value === fromValue) || { value: fromValue }
	return list
		.filter((el) => el.value && el.value !== fromValue)
		.map((target) => ({ current, target, event: 'approval' }))
}

/**
 * 현재 상태(fromValue) 기준 허용 전이 목록 생성.
 * @param {string[]} statusTransitions - 허용된 전이 feature_key 배열
 * @param {string} fromValue - 현재 상태값 (예: WORK_REQUEST)
 * @param {Array} projectStatuses - status.project 배열 ([{value,text,color,order}, ...])
 * @returns {Array<{current, target, event}>}
 */
function deriveAvailableTransitions(statusTransitions, fromValue, projectStatuses) {
	// 와일드카드가 있으면 개별 목록을 보지 않고 전체 단계를 내려 준다
	if (hasStatusWildcard(statusTransitions)) {
		return deriveAdminAvailableTransitions(fromValue, projectStatuses)
	}
	const byValue = (v) => (projectStatuses || []).find((el) => el.value === v)
	const seen = new Set()
	const list = []
	for (const key of statusTransitions || []) {
		const t = parseTransitionKey(key)
		if (!t || t.from !== fromValue) continue
		const target = byValue(t.to)
		if (!target) continue
		const dedup = `${t.to}:${t.event}`
		if (seen.has(dedup)) continue
		seen.add(dedup)
		list.push({ current: byValue(t.from), target, event: t.event })
	}
	return list
}

/** 역할별 레거시 전이 feature_key 목록 */
function legacyStatusTransitionsForRole(role) {
	if (!role) return []
	return LEGACY_TRANSITIONS.filter((t) => t.role === role).map((t) => t.key)
}

module.exports = {
	STATUS_WILDCARD,
	hasStatusWildcard,
	LEGACY_TRANSITIONS,
	parseTransitionKey,
	normalizeEvent,
	deriveAvailableTransitions,
	deriveAdminAvailableTransitions,
	legacyStatusTransitionsForRole,
}
